import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const AdminQuotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchQuotes(token);
  }, [navigate]);

  const fetchQuotes = async (token) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/quotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette demande de devis ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${BACKEND_URL}/api/admin/quotes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setQuotes(quotes.filter(q => q._id !== id));
        toast.success('Devis supprimé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: '48px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Link to="/admin/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--brand-dark)', textDecoration: 'none', marginBottom: '24px' }}>
          <ArrowLeft size={20} />
          <span>Retour au dashboard</span>
        </Link>

        <h1 style={{ fontSize: '36px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
          Demandes de Devis
        </h1>
        <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '48px' }}>
          {loading ? 'Chargement...' : `${quotes.length} demande(s) reçue(s)`}
        </p>

        {loading ? (
          <p>Chargement...</p>
        ) : quotes.length === 0 ? (
          <div className="network-card" style={{ textAlign: 'center', padding: '64px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '18px' }}>Aucune demande de devis pour le moment</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {quotes.map((quote) => (
              <div key={quote._id} className="network-card" style={{ padding: 'var(--spacing-large)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
                  <div>
                    <h3 style={{ fontSize: '24px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      {quote.nom} {quote.prenom}
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        background: 'var(--brand-primary)', 
                        color: 'var(--brand-dark)', 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        {quote.typeProjet}
                      </span>
                      <span style={{ 
                        background: 'var(--bg-subtle)', 
                        color: 'var(--text-primary)', 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '14px'
                      }}>
                        {quote.typeLogement}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(quote._id)}
                    style={{
                      background: '#ff4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Trash2 size={16} />
                    Supprimer
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-light)', marginBottom: '12px', textTransform: 'uppercase' }}>
                      Contact
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Mail size={16} style={{ color: 'var(--brand-primary)' }} />
                        <a href={`mailto:${quote.email}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                          {quote.email}
                        </a>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Phone size={16} style={{ color: 'var(--brand-primary)' }} />
                        <a href={`tel:${quote.telephone}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                          {quote.telephone}
                        </a>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={16} style={{ color: 'var(--brand-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {quote.codePostal} {quote.ville}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-light)', marginBottom: '12px', textTransform: 'uppercase' }}>
                      Projet
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: 'var(--text-secondary)' }}>
                      <p><strong>Propriétaire:</strong> {quote.proprietaire}</p>
                      <p><strong>Délai:</strong> {quote.delai}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Calendar size={16} style={{ color: 'var(--brand-primary)' }} />
                        <span style={{ fontSize: '14px' }}>
                          {new Date(quote.createdAt).toLocaleDateString('fr-FR', { 
                            day: '2-digit', 
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuotes;
